import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ktx2Loader } from '../render/ktx2';
import { afterFirstView } from '../render/shaderWarmup';
import { carModelDecoderReady, parseCarModel, type CarModelHeader, type ParsedCarModel } from './carModelFormat';
import { getVehicleParts, type ExtraLabel, type VehicleParts } from './meshes';
import type { VehicleBodyStyle, VehicleSpec } from './types';

/**
 * Detailed (Meshy) car bodies: docs/research/MESHY-WORLD-PLAN.md P5. The pilot was the Halberd Regent (the
 * `saloon` body: the Regent and the Regent Patrol); the Minnow, Hackney, Haulmaster, Bedwell Tipper and Vesper GT
 * followed the same pipeline. scripts/build-car-model.ts turns the Meshy model into
 * public/models/<id>.bin plus a base colour and a paint/chrome mask per LOD; this module loads them once, in the
 * background, and assembles a body per spec that the vehicle view draws instead of the procedural one:
 *
 * - the Meshy body (paint from the mask, so garage paint, finishes, the vinyl roof and the gang livery work),
 *   with lamp channels, bonnet / boot / bumper tags and dark arch liners from the build;
 * - merged with the spec's procedural cabin (seats, dash, wheel and the driver, shown through the glass), its
 *   number plates (moved onto the model's plate spots: the shader font), its roof parts (roof / ladder rack, the
 *   cab's TAXI sign) and, on the patrol car, the blue lamp and the roof sign. One mesh, one draw, as before;
 * - the secondary paint drawn by the shader on the painted texels (`uMeshy`): the patrol band, the Haulmaster's
 *   trade band, the Vesper's racing stripes and the Bedwell's tipper body;
 * - the Meshy windows as the glass mesh (panes break as before).
 *
 * The far impostor, the wheels (instanced), the road light and every other car stay procedural, and LOW keeps the
 * procedural body. Until the files arrive, or if they fail, cars draw procedurally: nothing waits on this.
 */

/** Body styles drawn with a detailed model, and the model's id (public/models/<id>.bin). */
export const MESHY_CAR_MODELS: Partial<Record<VehicleBodyStyle, string>> = {
  saloon: 'car-regent',
  hatch: 'car-minnow',
  cab: 'car-hackney',
  van: 'car-haulmaster',
  lorry: 'car-bedwell',
  coupe: 'car-vesper',
  // The three newer cars, built as Meshy models from the start (procedural bodies are their LOW / fallback).
  estate: 'car-drover',
  exec: 'car-grandee',
  roadster: 'car-linnet',
};

/**
 * `uMeshy.w`: which painted texels take the secondary paint (`uPaint2`). PATROL is always on (police blue); the
 * others follow the car's own secondary paint (`uShow.w`: trade band, stripes, tipper bed colour).
 */
export const MESHY_SECONDARY = { NONE: 0, PATROL: 1, BAND: 2, STRIPES: 3, BED: 4 } as const;

export interface MeshyCarMaps {
  color: THREE.Texture;
  mask: THREE.Texture;
}

export interface MeshyCarParts {
  /** Detailed body (HIGH) and the mid body (HIGH beyond 60 m, the near body on MEDIUM). */
  body: THREE.BufferGeometry;
  mid: THREE.BufferGeometry;
  glass: THREE.BufferGeometry;
  glassMid: THREE.BufferGeometry;
  bonnetHinge: [number, number] | null;
  bootHinge: [number, number] | null;
  bumperF: [number, number, number];
  bumperR: [number, number, number];
  /**
   * Shader `uMeshy`: x = roof line (paint above it is the roof); w = secondary paint mode (MESHY_SECONDARY) with
   * y / z its band heights (patrol, trade band), stripe edges |x| (stripes) or y the tipper body's front z (bed).
   */
  meshy: THREE.Vector4;
  /**
   * Base colour + mask of each body (each has its own atlas), null until loaded: MEDIUM loads only the mid ones.
   * Shared with the loader, so a rig sees them arrive.
   */
  maps: { high: MeshyCarMaps | null; mid: MeshyCarMaps | null };
  triangles: number;
  trianglesMid: number;
}

/** Bumped whenever a model or a texture set finishes loading: rigs compare it to swap their body in. */
export const meshyCars = { version: 0 };

interface Loaded {
  model: ParsedCarModel;
  maps: MeshyCarParts['maps'];
}
const loaded = new Map<string, Loaded>();
const pending = new Set<string>();
const failed = new Set<string>();
const assembled = new Map<string, MeshyCarParts>();

const BASE = import.meta.env?.BASE_URL ?? './';
/** URL of a file in public/models. */
function modelUrl(file: string): string {
  return `${BASE}models/${file}`;
}

async function loadMaps(files: [string, string]): Promise<MeshyCarMaps> {
  // KTX2 as shipped (scripts/compress-models.mjs: compressed in GPU memory, its own mips); a version-1 build's
  // JPEG / PNG still loads.
  const load = (f: string): Promise<THREE.Texture> => (f.endsWith('.ktx2') ? ktx2Loader() : new THREE.TextureLoader()).loadAsync(modelUrl(f));
  const [color, mask] = await Promise.all(files.map(load));
  // GLB convention: the atlas origin is the top-left corner (KTX2 is stored that way and never flipped).
  for (const t of [color!, mask!]) {
    t.flipY = false;
    t.needsUpdate = true;
  }
  color!.colorSpace = THREE.SRGBColorSpace;
  mask!.colorSpace = THREE.NoColorSpace;
  color!.anisotropy = 4;
  return { color: color!, mask: mask! };
}

/**
 * Start loading every detailed car (idempotent): the meshes and the mid textures, plus the HIGH textures when
 * `hi`. Errors are logged once and the cars stay procedural.
 */
export function requestMeshyCars(hi: boolean): void {
  if (typeof fetch === 'undefined' || typeof document === 'undefined') return;
  for (const id of new Set(Object.values(MESHY_CAR_MODELS))) {
    if (!id || failed.has(id)) continue;
    const l = loaded.get(id);
    const key = l ? `${id}:hi` : id;
    if (pending.has(key) || (l && (!hi || l.maps.high))) continue;
    pending.add(key);
    const job = l
      ? loadMaps(l.model.header.textures.high).then((m) => {
          l.maps.high = m;
        })
      : (async () => {
          // after the first view's textures (render/shaderWarmup.ts afterFirstView)
          await afterFirstView();
          const res = await fetch(modelUrl(`${id}.bin`));
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const bytes = await res.arrayBuffer();
          await carModelDecoderReady;
          const model = parseCarModel(bytes);
          const t = model.header.textures;
          const [mid, high] = await Promise.all([loadMaps(t.mid), hi ? loadMaps(t.high) : Promise.resolve(null)]);
          loaded.set(id, { model, maps: { high, mid } });
        })();
    void job
      .then(() => {
        meshyCars.version++;
      })
      .catch((e: unknown) => {
        failed.add(id);
        console.warn(`detailed car ${id} not loaded, the procedural body stays:`, e);
      })
      .finally(() => pending.delete(key));
  }
}

/** For tests and tools: register an already parsed model (textures optional). */
export function registerMeshyCar(id: string, model: ParsedCarModel, maps: MeshyCarParts['maps'] = { high: null, mid: null }): void {
  loaded.set(id, { model, maps });
  for (const k of [...assembled.keys()]) if (k.startsWith(`${id}:`)) assembled.delete(k);
  meshyCars.version++;
}

/** The detailed body for this spec, or null (no model for its body style, or not loaded yet, or failed). */
export function meshyCarParts(spec: VehicleSpec): MeshyCarParts | null {
  const id = MESHY_CAR_MODELS[spec.body];
  if (!id) return null;
  const l = loaded.get(id);
  if (!l) return null;
  const key = `${id}:${spec.id}`;
  let p = assembled.get(key);
  if (!p) {
    p = assembleMeshyCar(spec, l.model.header, l.model, l.maps);
    assembled.set(key, p);
  }
  return p;
}

/** A procedural group as an indexed copy, each vertex moved by `move`. */
function extra(g: THREE.BufferGeometry | null | undefined, move: (p: THREE.Vector3) => void): THREE.BufferGeometry | null {
  if (!g) return null;
  const c = g.clone();
  const pos = c.getAttribute('position') as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    move(v);
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  if (!c.index) {
    const n = pos.count;
    const idx = new Uint32Array(n);
    for (let i = 0; i < n; i++) idx[i] = i;
    c.setIndex(new THREE.BufferAttribute(idx, 1));
  }
  return c;
}

/** Shift that puts a plate group's face on the model's plate spot (front: its max z; rear: its min z). */
function plateShift(g: THREE.BufferGeometry | undefined, at: [number, number, number], front: boolean): (p: THREE.Vector3) => void {
  if (!g) return () => {};
  g.computeBoundingBox();
  const b = g.boundingBox!;
  const dx = at[0] - (b.min.x + b.max.x) / 2;
  const dy = at[1] - (b.min.y + b.max.y) / 2;
  const dz = at[2] + (front ? 0.012 : -0.012) - (front ? b.max.z : b.min.z);
  return (p) => p.set(p.x + dx, p.y + dy, p.z + dz);
}

/**
 * Assemble one spec's detailed body from a parsed model (pure: used by the loader and by the tests). The
 * patrol car keeps the Regent's body (a constabulary saloon is a standard saloon): its cabin and roof parts,
 * built for its slightly taller procedural body, are lowered onto the model's belt and roof.
 */
export function assembleMeshyCar(spec: VehicleSpec, header: CarModelHeader, model: Pick<ParsedCarModel, 'high' | 'mid'>, maps: MeshyCarParts['maps']): MeshyCarParts {
  const proc: VehicleParts = getVehicleParts(spec);
  const a = header.anchors;
  const ex = (k: ExtraLabel) => proc.extras[k];
  // The cabin is fitted between the model's screens (the dash behind the windscreen, the parcel shelf in front
  // of the rear screen) and onto its belt. Where the model's rear glass is not the procedural one (a van's rear
  // door windows, a lorry's cab) the cabin is only moved behind the windscreen.
  const dyCabin = a.beltY - proc.beltY;
  const [pr0, pf0] = proc.screens;
  const span = pf0 - pr0 || 1;
  const kzFit = (a.screenF - a.screenR) / span;
  const kz = Math.abs(kzFit - 1) < 0.3 ? kzFit : 1;
  // Nothing of it reaches past the windscreen's base, and the dash stays under the cowl (a lorry's dash poked out
  // of its flatter cab front, the Vesper's through the back of its bonnet).
  const cabin = (p: THREE.Vector3) => {
    const z = Math.min(a.screenF - 0.03, a.screenF + (p.z - pf0) * kz);
    const y = p.y + dyCabin;
    return p.set(p.x, z > a.screenF - 0.2 ? Math.min(y, a.beltY - 0.03) : y, z);
  };
  const dyRoof = a.roofY - proc.roofY;
  const roof = (p: THREE.Vector3) => p.set(p.x, p.y + dyRoof, p.z);
  const pf = plateShift(ex('plateF')?.full, a.plateF, true);
  const pr = plateShift(ex('plateR')?.full, a.plateR, false);
  const merge = (parts: (THREE.BufferGeometry | null)[]): THREE.BufferGeometry => {
    const list = parts.filter((p): p is THREE.BufferGeometry => p !== null);
    const g = mergeGeometries(list)!;
    for (const p of list.slice(1)) p.dispose();
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  };
  const body = merge([
    model.high.body.clone(),
    extra(ex('cabin')?.full, cabin),
    extra(ex('plateF')?.full, pf),
    extra(ex('plateR')?.full, pr),
    extra(ex('police')?.full, roof),
    extra(ex('roof')?.full, roof),
  ]);
  const mid = merge([
    model.mid.body.clone(),
    extra(ex('plateF')?.mid ?? ex('plateF')?.full, pf),
    extra(ex('plateR')?.mid ?? ex('plateR')?.full, pr),
    extra(ex('police')?.mid ?? ex('police')?.full, roof),
    extra(ex('roof')?.mid, roof),
  ]);
  for (const g of [model.high.glass, model.mid.glass]) g.computeBoundingSphere();
  // Secondary paint, where meshes.ts draws it on the procedural body.
  const S = MESHY_SECONDARY;
  const meshy = new THREE.Vector4(a.roofLine, 0, 0, S.NONE);
  if (spec.class === 'police') {
    // The patrol car's blue waist band: 20 cm under the belt, 13 cm deep.
    const bandY = a.beltY - 0.2;
    meshy.set(a.roofLine, bandY - 0.065, bandY + 0.065, S.PATROL);
  } else if (spec.body === 'van') meshy.set(a.roofLine, 0.56 * spec.height - 0.05, 0.56 * spec.height + 0.05, S.BAND);
  else if (spec.body === 'coupe') meshy.set(a.roofLine, 0.065, 0.195, S.STRIPES);
  // Everything painted behind the cab (its back is the procedural cabin's rear end) is the tipper body.
  else if (spec.body === 'lorry') meshy.set(a.roofLine, pr0 - 0.08, 0, S.BED);
  return {
    body,
    mid,
    glass: model.high.glass,
    glassMid: model.mid.glass,
    bonnetHinge: a.bonnetHinge,
    bootHinge: a.bootHinge,
    bumperF: a.bumperF,
    bumperR: a.bumperR,
    meshy,
    maps,
    triangles: body.index!.count / 3,
    trianglesMid: mid.index!.count / 3,
  };
}

/** Test hook: forget everything loaded. */
export function resetMeshyCars(): void {
  loaded.clear();
  pending.clear();
  failed.clear();
  assembled.clear();
}
